import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Sparkles, AlertCircle, Play, Image as ImageIcon, ArrowLeft } from 'lucide-react';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { useAppStore } from '../../store/useAppStore';
import { getCreatorPublicProfile, logCreatorLandingVisit } from '../../utils/creatorApi';
import type { CreatorPublicProfile } from '../../types/creator';

export function CreatorPublicLandingPage() {
  const { handle, slug } = useParams<{ handle: string; slug: string }>();
  const isAuthenticated = useAppStore((s) => s.isAuthenticated);
  const [profile, setProfile] = useState<CreatorPublicProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!handle) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    void (async () => {
      const data = await getCreatorPublicProfile(handle);
      if (cancelled) return;
      if (!data) {
        setError('Creator not found');
      } else {
        setProfile(data);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [handle]);

  const template = useMemo(
    () => profile?.templates.find((t) => t.slug === slug) ?? null,
    [profile, slug]
  );

  useEffect(() => {
    if (!template || !handle) return;
    void logCreatorLandingVisit({
      handle,
      templateSlug: template.slug ?? null,
      templateId: template.id,
      referrer: typeof document !== 'undefined' ? document.referrer || null : null,
    });
  }, [template, handle]);

  if (loading) {
    return (
      <div className="min-h-dvh flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="min-h-dvh flex flex-col items-center justify-center p-6 text-center gap-3">
        <AlertCircle className="w-8 h-8 text-status-error" />
        <p className="text-th-secondary">{error || 'Creator not available'}</p>
        <Link to="/" className="text-brand hover:underline">
          Back to store
        </Link>
      </div>
    );
  }

  if (!template) {
    return (
      <div className="min-h-dvh max-w-3xl mx-auto px-4 py-12 text-center space-y-4">
        <p className="text-th-secondary">No live agent matches that link.</p>
        <Link to={`/c/${handle}`} className="text-brand hover:underline">
          See all agents by @{handle}
        </Link>
      </div>
    );
  }

  const heroTitle = template.public_title || template.name;
  const heroSub = template.public_description || '';
  const startUrl = isAuthenticated ? `/apps/agent/${template.id}` : `/login?next=/apps/agent/${template.id}`;

  return (
    <div className="min-h-dvh max-w-4xl mx-auto px-4 pt-6 pb-20">
      <Link
        to={`/c/${handle}`}
        className="text-th-secondary hover:text-th-primary inline-flex items-center gap-1 text-sm mb-4"
      >
        <ArrowLeft className="w-4 h-4" /> Back to @{handle}
      </Link>

      <article className="card overflow-hidden">
        <div className="relative">
          {template.cover_media_url && template.cover_media_type === 'image' ? (
            <img src={template.cover_media_url} alt="" className="w-full h-56 sm:h-72 object-cover" />
          ) : template.cover_media_url && template.cover_media_type === 'video' ? (
            <video
              src={template.cover_media_url}
              className="w-full h-56 sm:h-72 object-cover"
              autoPlay
              loop
              muted
              playsInline
            />
          ) : (
            <div className="w-full h-56 sm:h-72 flex items-center justify-center bg-elevated text-th-tertiary">
              <ImageIcon className="w-10 h-10" />
            </div>
          )}
        </div>
        <div className="p-6 space-y-4">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <p className="text-xs text-th-secondary font-mono">
                by{' '}
                <Link to={`/c/${handle}`} className="hover:underline">
                  @{handle}
                </Link>
              </p>
              <h1 className="text-2xl sm:text-3xl font-bold text-th-primary tracking-tight mt-1">
                {heroTitle}
              </h1>
            </div>
            <Link
              to={startUrl}
              className="button-primary inline-flex items-center gap-2 shrink-0"
            >
              <Play className="w-4 h-4" />
              Start chat
            </Link>
          </div>
          {heroSub && (
            <p className="text-sm text-th-secondary whitespace-pre-wrap leading-relaxed">{heroSub}</p>
          )}
          {(template.tags?.length ?? 0) > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {(template.tags || []).map((tag) => (
                <span
                  key={tag}
                  className="text-[11px] uppercase tracking-wide px-2.5 py-0.5 rounded-full bg-overlay text-th-secondary"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
      </article>

      <section className="mt-8">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-th-secondary mb-2">
          More from @{handle}
        </h2>
        <div className="grid sm:grid-cols-2 gap-3">
          {profile.templates
            .filter((t) => t.id !== template.id)
            .slice(0, 4)
            .map((t) => (
              <Link
                key={t.id}
                to={t.slug ? `/c/${handle}/${t.slug}` : `/apps/agent/${t.id}`}
                className="card p-4 flex items-start gap-3 hover:border-brand/40 transition-colors"
              >
                <div className="w-10 h-10 rounded-lg bg-elevated overflow-hidden flex items-center justify-center shrink-0">
                  {t.cover_media_url && t.cover_media_type === 'image' ? (
                    <img src={t.cover_media_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <Sparkles className="w-4 h-4 text-brand" />
                  )}
                </div>
                <div className="min-w-0">
                  <h3 className="font-semibold text-th-primary truncate text-sm">
                    {t.public_title || t.name}
                  </h3>
                  {t.public_description && (
                    <p className="text-xs text-th-secondary line-clamp-2 mt-0.5">
                      {t.public_description}
                    </p>
                  )}
                </div>
              </Link>
            ))}
        </div>
      </section>
    </div>
  );
}
